import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/salesRequestMutations"
import { formatRequestIdSuffix } from "../utils/format"

import type { CreateSalesRequestMutation, SalesRequestCreateInput } from "@/graphql/codegen/graphql"
import { CreateSalesRequestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface CreateSalesRequestOptions {
  getSuccessMessage?: (requestIds: string[]) => string
  // skip success toast — this call only creates a background DRAFT (e.g. "Continue to Email")
  silent?: boolean
}

export interface CreateSalesRequestResult {
  success: boolean
  /** Shared id for every product row created by the call — undefined on failure. */
  quoteGroupId?: number
}

interface UseCreateSalesRequestReturn {
  createSalesRequest: (
    input: SalesRequestCreateInput,
    options?: CreateSalesRequestOptions
  ) => Promise<CreateSalesRequestResult>
  isCreating: boolean
}

const getDefaultSuccessMessage = (requestIds: string[]): string =>
  `Request successfully Sent! ${formatRequestIdSuffix(requestIds)}`

export const useCreateSalesRequest = (): UseCreateSalesRequestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<CreateSalesRequestMutation, { input: SalesRequestCreateInput }>(
    CreateSalesRequestDocument
  )

  const createSalesRequest = useCallback(
    async (
      input: SalesRequestCreateInput,
      options: CreateSalesRequestOptions = {}
    ): Promise<CreateSalesRequestResult> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.salesPipeline.createRequest

      if (error || output?.__typename === "SalesRequestCreateFailure") {
        const message =
          output?.__typename === "SalesRequestCreateFailure"
            ? output.error.message
            : (error?.message ?? "Failed to submit request. Please try again.")
        notify({ type: NotificationType.Error, message })
        return { success: false }
      }

      if (output?.__typename !== "SalesRequestCreateSuccess") {
        notify({ type: NotificationType.Error, message: "Failed to submit request. Please try again." })
        return { success: false }
      }

      const requestIds = output.items
        .map((item) => item.salesPipelineQuoteRequestId)
        .filter((id): id is string => Boolean(id))
      if (!options.silent) {
        const getSuccessMessage = options.getSuccessMessage ?? getDefaultSuccessMessage
        notify({ type: NotificationType.Success, message: getSuccessMessage(requestIds) })
      }
      return { success: true, quoteGroupId: output.items[0]?.quoteGroupId }
    },
    [executeMutation, notify]
  )

  return { createSalesRequest, isCreating: fetching }
}
